import React, { useState } from "react";
import axios from "axios";
import { Mail, MapPin, Linkedin, Phone, Send, Check } from "lucide-react";
import { useSiteContent } from "../data/content";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const content = useSiteContent();
  const { contact, cta } = content;
  const ui = content.ui.contact;
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: ui.form.validationError });
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        company: form.company || null,
        message: form.message,
      });
      setSent(true);
      toast({
        title: ui.form.successTitle,
        description: data?.email_sent ? ui.form.successDesc : ui.form.savedDesc,
      });
      setForm({ name: "", email: "", company: "", message: "" });
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      const msg = err?.response?.data?.detail || ui.form.errorDesc;
      toast({ title: ui.form.errorTitle, description: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="rounded-3xl bg-gradient-to-br from-slate-50 to-white border border-slate-200 p-8 lg:p-14 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <span className="text-[12px] font-medium tracking-[0.2em] uppercase text-teal-700">
              {ui.eyebrow}
            </span>
            <h2 className="mt-4 text-3xl lg:text-4xl font-semibold tracking-[-0.02em] text-[#0B1F3A] leading-[1.15]">
              {cta.title}
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">{cta.subtitle}</p>

            <ul className="mt-10 space-y-5">
              <li className="flex items-start gap-4">
                <span className="w-10 h-10 rounded-lg bg-white border border-slate-200 text-[#0B1F3A] flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">{ui.labels.location}</div>
                  <div className="text-[#0B1F3A] font-medium">{contact.location}</div>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-10 h-10 rounded-lg bg-white border border-slate-200 text-[#0B1F3A] flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">{ui.labels.email}</div>
                  <a href={`mailto:${contact.email}`} className="text-[#0B1F3A] font-medium hover:text-teal-700">
                    {contact.email}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-10 h-10 rounded-lg bg-white border border-slate-200 text-[#0B1F3A] flex items-center justify-center shrink-0">
                  <Linkedin className="w-5 h-5" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">{ui.labels.linkedin}</div>
                  <a href={contact.linkedin} target="_blank" rel="noreferrer" className="text-[#0B1F3A] font-medium hover:text-teal-700">
                    Stefan Liesegang
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-10 h-10 rounded-lg bg-white border border-slate-200 text-[#0B1F3A] flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5" />
                </span>
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-500">{ui.labels.phone}</div>
                  <div className="text-[#0B1F3A] font-medium">{contact.phone}</div>
                </div>
              </li>
            </ul>
          </div>

          <form onSubmit={onSubmit} className="lg:col-span-7 bg-white rounded-2xl border border-slate-200 p-6 lg:p-8 shadow-sm">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={ui.form.name} value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <Field label={ui.form.email} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            </div>
            <div className="mt-4">
              <Field label={ui.form.company} value={form.company} onChange={(v) => setForm({ ...form, company: v })} />
            </div>
            <div className="mt-4">
              <label className="block text-xs font-medium text-slate-600 mb-1.5">{ui.form.message}</label>
              <textarea
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={ui.form.placeholder}
                className="w-full rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 focus:bg-white transition-colors"
              />
            </div>
            <div className="mt-6 flex items-center justify-between">
              <p className="text-xs text-slate-500">{ui.form.consent}</p>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 bg-[#0B1F3A] hover:bg-[#102a4d] disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-medium px-5 py-3 rounded-lg transition-colors"
              >
                {sent ? (
                  <>
                    <Check className="w-4 h-4" /> {ui.form.sent}
                  </>
                ) : loading ? (
                  <>{ui.form.sending}</>
                ) : (
                  <>
                    <Send className="w-4 h-4" /> {ui.form.submit}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1.5">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-slate-200 bg-slate-50/60 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-500 focus:bg-white transition-colors"
      />
    </div>
  );
}
